import React, { useEffect, useRef, useState } from "react";
import { Button, Spinner } from "reactstrap";

import defaultImage from "assets/img/placeholder.png";

import { actualFileType } from "./ImageUtils";
import { failAlert } from "../../../../utils/alertUtils";

import imageCompression from "browser-image-compression";

const SingleImageUpload = ({
	containerWidth,
	file,
	setFile,
	disablePhotoChange,
	picData,
	setPicData,
}) => {
	const fileInputRef = useRef(null);
	const [imagePreviewUrl, setImagePreviewUrl] = useState(defaultImage);
	const [isCompressing, setIsCompressing] = useState(false);

	// Calls the onClick function of the file input element
	const updateInput = () => fileInputRef.current.click();

	// Resets the image container to default image and removes any uploaded file
	const removeImage = () => {
		setFile(null);
		setImagePreviewUrl(defaultImage);

		if (picData != null) {
			setPicData(null);
		}
	};

	// Called when an image is selected for upload
	const uploadImage = (e) => {
		e.preventDefault();

		let reader = new FileReader();
		let upload = e.target.files[0];

		if (upload != null) {
			reader.readAsArrayBuffer(upload);
			reader.onloadend = (e) => fileLoaded(e, upload);
			reader.onerror = fileError;
		}
	};

	// Handler for when a file has successfully been loaded into the input element.
	// Throws error if file is not an accepted type.
	const fileLoaded = async (e, upload) => {
		var arr = new Uint8Array(e.target.result).subarray(0, 4);
		var header = "";

		for (var i = 0; i < arr.length; i++) {
			header += arr[i].toString(16);
		}

		const acceptedFileType = actualFileType(header);

		if (acceptedFileType === upload.type) {
			await compressAndAccept(upload);
		} else {
			failAlert("Only JPEG/JPG and PNG files are allowed");
			setFile(null);
			setImagePreviewUrl(defaultImage);
		}
	};

	// Compress and process accepted image
	const compressAndAccept = async (file) => {
		try {
			setIsCompressing(true);

			const options = {
				maxSizeMB: 1,
				maxWidthOrHeight: 1920,
				useWebWorker: true,
			};

			const compressedFile = await imageCompression(file, options);

			console.log(
				`Compressed ${file.name}: from ${(file.size / 1024 / 1024).toFixed(
					2
				)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`
			);

			const reader = new FileReader();
			reader.readAsDataURL(compressedFile);
			reader.onload = (e) => fileAccepted(e, compressedFile);
		} catch (err) {
			console.error("Compression error:", err);
			fileAccepted(null, file); // fallback to original
		} finally {
			setIsCompressing(false);
		}
	};

	// Handler for processing an accepted image file.
	const fileAccepted = (e, upload) => {
		const imageData = e ? e.target.result : URL.createObjectURL(upload);
		var image = new Image();

		image.src = imageData;

		image.onload = () => {
			setFile(upload);
			setImagePreviewUrl(imageData);
			setPicData(imageData);
		};
	};

	// Called when there is an error in the file upload process
	const fileError = (e) => {
		if (e.target.error.name === "NotReadableError") {
			failAlert("File could not be read, please try again");
		} else {
			failAlert(e?.target?.error?.name || "Unexpected error");
		}

		setFile(null);
	};

	useEffect(() => {
		if (picData != null) {
			setImagePreviewUrl(picData);
		}
	}, [picData]);

	return (
		<div className={`fileinput text-center ${containerWidth || "w-100"}`}>
			<input
				ref={fileInputRef}
				type="file"
				onChange={uploadImage}
				accept=".png, .jpeg, .jpg, .webp"
			/>
			<div className="thumbnail">
				{isCompressing ? (
					<div className="d-flex justify-content-center align-items-center gap-2">
						<Spinner size="sm" color="primary" />
						<span>Compressing…</span>
					</div>
				) : (
					<img src={imagePreviewUrl} alt="imageUpload" />
				)}
			</div>
			{disablePhotoChange === false && (
				<div>
					{file == null && picData == null && (
						<Button className="btn-round btn-primary" onClick={updateInput}>
							Select image
						</Button>
					)}
					{(file != null || picData != null) && (
						<span>
							<Button className="btn-round btn-primary" onClick={updateInput}>
								Change
							</Button>
							<Button
								color="danger"
								className="btn-round btn-primary"
								onClick={removeImage}
							>
								<i className="fa fa-times" />
								Remove
							</Button>
						</span>
					)}
				</div>
			)}
		</div>
	);
};

export default SingleImageUpload;
